import { createSlice } from '@reduxjs/toolkit';

/*
 * This, outreachLists, is where we keep track of "Outreaches" to or from particular people.
 * The people in question may be clients, referral contacts, or emergency contacts.
 *
 * The structure is
 * {
 *   <personIdI>: [
 *     {
 *       outreachId: <pId>,
 *       firmId: <fId>,
 *       date: <millis>,
 *       descrip: 'something',
 *       senderId: <sId>,
 *       receiverId, <rId>,
 *       dlId: <dId>,
 *       direction: <['To' | 'From']>
 *       missiveId: <mId>,
 *     },
 *     {
 *       outreachId: <pId>,
 *       firmId: <fId>,
 *       date: <millis>,
 *       ...
 *     },
 *     ...
 *   ],
 *   <personIdJ>: [
 *     {
 *       outreachId: <pId>,
 *       firmId: <fId>,
 *       date: <millis>,
 *       ...
 *     },
 *     ...
 *   ],
 *   ...
 * }
 */

const initialState = {};

const haveListForPerson = (state, personId) => Object.keys(state).indexOf(personId.toString()) >= 0;

export const outreachListsSlice = createSlice({
  name: 'outreachLists',
  initialState: initialState,
  reducers: {
    clearOutreaches: () => {
      return initialState;
    },

    addOutreaches: (state, action) => {
      const { personId, outreaches } = action.payload;
      if (haveListForPerson(state, personId)) {
        let modified = false;
        let listOfOutreaches = state[personId];
        outreaches.forEach((outreach) => {
          if (!listOfOutreaches.some((or) => or.outreachId === outreach.outreachId)) {
            modified = true;
            listOfOutreaches = [...listOfOutreaches, outreach];
          }
        });
        if (modified) {
          return { ...state, [personId]: listOfOutreaches };
        } else {
          return state;
        }
      } else {
        return { ...state, [personId]: outreaches };
      }
    },

    updateOutreach: (state, action) => {
      const { personId, outreach } = action.payload;
      if (haveListForPerson(state, personId)) {
        const prunedList = state[personId].filter((or) => or.outreachId !== outreach.outreachId);
        const newList = [...prunedList, outreach];
        return { ...state, [personId]: newList };
      } else {
        return state;
      }
    },

    removeOutreach: (state, action) => {
      const { personId, outreachId } = action.payload;
      if (haveListForPerson(state, personId)) {
        const newList = state[personId].filter((or) => or.outreachId !== outreachId);
        return { ...state, [personId]: newList };
      } else {
        return state;
      }
    },

    removeOutreachFrom: (state, action) => {
      const { personId, outreachId } = action.payload;
      const keys = Object.keys(state);
      let newState = state;
      keys.forEach((key) => {
        const outreachesForKey = state[key];
        const match = outreachesForKey.find((or) => or.outreachId === outreachId && or.senderId === personId);
        if (match) {
          const filteredList = outreachesForKey.filter((or) => or.outreachId !== outreachId);
          newState = { ...state, [key]: filteredList };
        }
      });
      return newState;
    },

    switchOutreachList: (state, action) => {
      const { outreach, oldContactId, newContactId } = action.payload;
      const outreachId = outreach.outreachId;
      const listOfOutreachesToOld = state[oldContactId] || [];
      const listOfOutreachesToNew = state[newContactId] || [];
      const prunedOutreachesToOld = listOfOutreachesToOld.filter((o) => o.outreachId !== outreachId);
      const augmentedOutreachesToNew = [...listOfOutreachesToNew, outreach];
      return { ...state, [oldContactId]: prunedOutreachesToOld, [newContactId]: augmentedOutreachesToNew };
    },
  },
});

export const {
  clearOutreaches,
  addOutreaches,
  updateOutreach,
  removeOutreach,
  removeOutreachFrom,
  switchOutreachList,
} = outreachListsSlice.actions;

export default outreachListsSlice.reducer;
